import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config, types } from 'helpers';


export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: config.server,
    credentials: 'include',
  }),
  endpoints: (build) => ({
    login: build.mutation<
      { data: types.User; },
      { email: string; password: string; }
    >({
      query: (body) => ({
        url: `auth/login`,
        method: 'POST',
        body,
      }),
    }),
    loginOtp: build.mutation<
      { token: string; },
      { email: string; code: string; }
    >({
      query: (body) => ({
        url: `auth/otp`,
        method: 'POST',
        body,
      }),
    }),
    reset: build.mutation<
      { message: string; },
      { email: string; }
    >({
      query: (body) => ({
        url: `auth/reset`,
        method: 'POST',
        body,
      }),
    }),
    resetConfirm: build.mutation<
      { message: string },
      { code: number; email: string; password: string; }
    >({
      query: (body) => ({
        url: `auth/reset`,
        method: 'PUT',
        body,
      }),
    }),
    signup: build.mutation<{ user: types.User; token: string; business: types.Business }, Partial<types.User>>({
      query: (body) => ({
        url: `auth/signup`,
        method: 'POST',
        body,
      }),
    }),
    acceptInvite: build.mutation<{ data: types.User; token: string; business: types.Business }, Partial<types.User>>({
      query: (body) => ({
        url: `business/invites/${body.id}`,
        method: 'POST',
        body,
      }),
    }),
    me: build.query<types.User, { id: string; cache?: string; }>({
      query: (query) => `users/${query.id}?cache=${query.cache}`,
    }),
    editUser: build.mutation<types.User, Partial<types.User>>({
      query: (body) => ({
        url: `users/${body.id}`,
        method: 'PUT',
        body,
      }),
    }),
    editPassword: build.mutation<types.User, { id: string; old_password: string; new_password: string; }>({
      query: (body) => ({
        url: `users/${body.id}/password`,
        method: 'PUT',
        body,
      }),
    }),

    business: build.query<types.Business, { id: string; cache?: string; }>({
      query: (query) => `business/${query.id}?cache=${query.cache}`,
    }),
    addBusiness: build.mutation<types.Business, Partial<types.Business>>({
      query: (body) => ({
        url: `business`,
        method: 'POST',
        body,
      }),
    }),
    editBusiness: build.mutation<types.Business, Partial<types.Business>>({
      query: (body) => ({
        url: `business/${body.id}`,
        method: 'PUT',
        body,
      }),
    }),
    topUpBusiness: build.mutation<types.Business, { id: string; amount: number }>({
      query: (body) => ({
        url: `business/${body.id}/topup`,
        method: 'PATCH',
        body,
      }),
    }),
    mposUsers: build.query<types.MposUser[], { businessId: string }>({
      query: ({ businessId }) => `mpos/users?businessId=${businessId}`,
    }),
    mposInviteUser: build.mutation<{ message: string }, { businessId: string; fullName: string; phoneNumber: string; email?: string }>({
      query: ({ businessId, ...body }) => ({
        url: `mpos/${businessId}/invite`,
        method: 'POST',
        body,
      }),
    }),
    mposUsersList: build.query<{ data: types.MposWorkspace[]; meta: typeof types.DefaultMeta }, { businessId: string; search?: string; status?: string; cursor?: string }>({
      query: ({ businessId, ...params }) => ({
        url: `mpos/${businessId}/users`,
        params,
      }),
    }),
    mposUserDetail: build.query<types.MposWorkspace, { businessId: string; userId: string }>({
      query: ({ businessId, userId }) => `mpos/${businessId}/users/${userId}`,
    }),
    mposUpdateUser: build.mutation<types.MposWorkspace, { businessId: string; userId: string; fullName?: string; email?: string }>({
      query: ({ businessId, userId, ...body }) => ({
        url: `mpos/${businessId}/users/${userId}`,
        method: 'PATCH',
        body,
      }),
    }),
    mposChangePhone: build.mutation<{ message: string }, { businessId: string; userId: string; phoneNumber: string }>({
      query: ({ businessId, userId, ...body }) => ({
        url: `mpos/${businessId}/users/${userId}/phone`,
        method: 'PATCH',
        body,
      }),
    }),
    mposDisableUser: build.mutation<{ message: string }, { businessId: string; userId: string }>({
      query: ({ businessId, userId }) => ({
        url: `mpos/${businessId}/users/${userId}/disable`,
        method: 'POST',
      }),
    }),
    mposEnableUser: build.mutation<{ message: string }, { businessId: string; userId: string }>({
      query: ({ businessId, userId }) => ({
        url: `mpos/${businessId}/users/${userId}/enable`,
        method: 'POST',
      }),
    }),
    mposTips: build.query<{ data: types.MposTip[]; meta: typeof types.DefaultMeta; stats: { totalValue: number; totalVolume: number } }, { businessId: string; cursor?: string; userId?: string; from?: string; to?: string }>({
      query: ({ businessId, ...params }) => ({
        url: `mpos/${businessId}/tips`,
        params,
      }),
    }),
    mposSettings: build.query<types.MposSettings, { businessId: string }>({
      query: ({ businessId }) => `mpos/${businessId}/settings`,
    }),
    mposUpdateSettings: build.mutation<types.MposSettings, { businessId: string } & Partial<types.MposSettings>>({
      query: ({ businessId, ...body }) => ({
        url: `mpos/${businessId}/settings`,
        method: 'PATCH',
        body,
      }),
    }),
    mposReverseCharge: build.mutation<{ reversalTransactionId: string }, { transactionId: string; reason: string; includesTip: boolean; isPartial: boolean; partialAmount?: number }>({
      query: ({ transactionId, ...body }) => ({
        url: `mpos/charge/${transactionId}/reverse`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginOtpMutation,
  useResetMutation,
  useResetConfirmMutation,
  useSignupMutation,
  useAcceptInviteMutation,
  useMeQuery,
  useLazyMeQuery,
  useBusinessQuery,
  useLazyBusinessQuery,
  useEditUserMutation,
  useEditPasswordMutation,
  useEditBusinessMutation,
  useAddBusinessMutation,
  useTopUpBusinessMutation,
  useMposUsersQuery,
  useMposInviteUserMutation,
  useMposUsersListQuery,
  useLazyMposUsersListQuery,
  useMposUserDetailQuery,
  useLazyMposUserDetailQuery,
  useMposUpdateUserMutation,
  useMposChangePhoneMutation,
  useMposDisableUserMutation,
  useMposEnableUserMutation,
  useMposTipsQuery,
  useLazyMposTipsQuery,
  useMposSettingsQuery,
  useMposUpdateSettingsMutation,
  useMposReverseChargeMutation,
} = authApi;
